import { useMemo } from 'react';
import { useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import type { Permission, Scope } from '../../api/types/permissions';
import type { PrincipalIdentifierLocal } from '../../api/types/PlatformLocal';
import type { AccountTypeLocal, UserFieldDefLocal } from '../../api/types/TenantLocal';
import { useAuth } from '../../hooks/useAuth';
import { useTenantUserFields } from '../../hooks/useTenantUserFields';
import { buildCustomFieldsSchema, pickCustomFieldsDefaults } from './userFieldsSchema';
import { buildIdentifierSchema } from './identifierFields';
import { apiUserIpAllowlistError } from '../../lib/apiUserIpAllowlist';
import { canActorAssignScope, groupRolesByScope, strongestActorScope } from '../../lib/roleScopes';
import type { LabelResolver } from '../../hooks/useLabelResolver';

/** Contrato mínimo del item de rol que el hook necesita (los endpoints pueden tipar el resto). */
export interface RoleItem {
    roleId: string;
    roleName: string;
    scope?: Scope;
    /** Permisos del rol; deciden si un sistema (API) puede tenerlo. */
    permissions?: readonly string[];
}

/** Values del form de alta. `email` y `phone` SIEMPRE presentes en RHF (uno queda `''` según el primario). */
export interface UserCreateFormValues {
    email: string;
    phone: string;
    displayName: string;
    roleId: string;
    customFields?: Record<string, string>;
    /** Solo del alta de un sistema (API). */
    technicalContact: string;
    ipAllowlist: string;
}

export interface UserCreateFormCoreConfig<TRole extends RoleItem> {
    /** Tenant del catálogo de roles + customFields (actor-tenant en tenant, path-tenant en platform). */
    catalogTenantId: string;
    /** Identificador primario del tenant, resuelto por el form (useMyTenantIdentifier / useTenantIdentifier). */
    principalIdentifier: PrincipalIdentifierLocal;
    /** queryKey de los roles (el form decide el namespace). */
    rolesQueryKey: readonly unknown[];
    /** Fetcher de roles agnóstico del endpoint (tenantApi.listRoles / platformApi.listTenantRoles). */
    listRoles: () => Promise<{ items: TRole[] }>;
    /** Permiso `*.user.create.lateral` del scope correspondiente. */
    lateralPermission: Permission;
    /** Resolvedor del tenant del catálogo; da los nombres de los grupos de roles. */
    labels?: LabelResolver;
    /** Persona o sistema (API): cambia los campos que se validan. Default persona. */
    accountType?: AccountTypeLocal;
}

export interface UserCreateFormCore<TRole extends RoleItem> {
    form: UseFormReturn<UserCreateFormValues>;
    /** Roles asignables agrupados por nivel para `<RoleSelect>`. */
    roleGroups: { key: string; label: string; roles: TRole[] }[];
    /** roleId elegido (watch). */
    roleId: string;
    /** scope REAL del rol elegido, SIN fallback (cada form aplica el suyo). */
    roleScopeRaw: Scope | undefined;
    /** Catálogo de campos personalizados (para `<DynamicUserFields>`). */
    customFields: UserFieldDefLocal[];
    actorScope: Scope | undefined;
    canLateral: boolean;
    /** Setea el rol validando (el form envuelve esto si necesita side-effects extra). */
    setRole: (id: string) => void;
}

/**
 * Núcleo COMPARTIDO de los forms de alta de usuario (tenant + platform). Centraliza lo duplicado:
 * schema (identifier + displayName + role + customFields), `useForm`, query+filtro+agrupado de roles,
 * resolución de `actorScope`/`canLateral`. NO conoce destino, submit ni navegación — eso queda en cada form.
 * Genérico sobre el item de rol (`TRole`) para no caer en `any`.
 */
export function useUserCreateFormCore<TRole extends RoleItem>(
    config: UserCreateFormCoreConfig<TRole>,
): UserCreateFormCore<TRole> {
    const {
        catalogTenantId,
        principalIdentifier,
        rolesQueryKey,
        listRoles,
        lateralPermission,
        labels,
        accountType = 'HUMAN',
    } = config;
    const { roleAssignments, hasPermission } = useAuth();

    const actorScope = strongestActorScope(roleAssignments);
    const canLateral = hasPermission(lateralPermission);

    const { data: customFields = [] } = useTenantUserFields(catalogTenantId, { enabledOnly: true });

    const schema = useMemo(
        () =>
            accountType === 'API'
                ? buildApiUserSchema()
                : buildIdentifierSchema(principalIdentifier).extend({
                      displayName: z.string().min(1),
                      roleId: z.string().min(1),
                      customFields: buildCustomFieldsSchema(customFields),
                  }),
        [accountType, principalIdentifier, customFields],
    );
    const customFieldsDefaults = useMemo(
        () => pickCustomFieldsDefaults(customFields),
        [customFields],
    );

    const form = useForm<UserCreateFormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            email: '',
            phone: '',
            displayName: '',
            roleId: '',
            customFields: {},
            technicalContact: '',
            ipAllowlist: '',
        },
        values: {
            email: '',
            phone: '',
            displayName: '',
            roleId: '',
            customFields: customFieldsDefaults,
            technicalContact: '',
            ipAllowlist: '',
        },
        resetOptions: { keepDirtyValues: true },
    });
    const roleId = useWatch({ control: form.control, name: 'roleId' });

    const { data: rolesData } = useQuery({
        queryKey: rolesQueryKey,
        queryFn: listRoles,
        enabled: !!catalogTenantId,
    });
    const roleItems = useMemo(() => rolesData?.items ?? [], [rolesData]);

    const assignableRoles = useMemo(
        () => roleItems.filter((r) => canActorAssignScope(actorScope, r.scope, canLateral)),
        [roleItems, actorScope, canLateral],
    );
    const roleGroups = useMemo(
        () => groupRolesByScope(assignableRoles, labels, catalogTenantId),
        [assignableRoles, labels, catalogTenantId],
    );

    const roleScopeRaw = useMemo(
        () => roleItems.find((r) => r.roleId === roleId)?.scope,
        [roleItems, roleId],
    );

    const setRole = (id: string) => {
        form.setValue('roleId', id, { shouldValidate: true });
    };

    return {
        form,
        roleGroups,
        roleId,
        roleScopeRaw,
        customFields,
        actorScope,
        canLateral,
        setRole,
    };
}

/** Schema del alta de un sistema (API): sin correo ni teléfono, con IPs obligatorias. */
function buildApiUserSchema() {
    return z.object({
        email: z.string(),
        phone: z.string(),
        displayName: z
            .string()
            .trim()
            .min(1, 'Escribe el nombre del sistema.')
            .max(120, 'Usa máximo 120 caracteres.'),
        roleId: z.string().min(1, 'Elige un rol.'),
        customFields: z.record(z.string()).optional(),
        technicalContact: z.string().trim().max(200, 'Usa máximo 200 caracteres.'),
        ipAllowlist: z.string().superRefine((raw, ctx) => {
            const message = apiUserIpAllowlistError(raw);
            if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
        }),
    });
}
